'use client';

import type { WelcomeMissionKind } from './welcomeMission.consts';
import type { UserQuestDto } from '@/queries/userQuest/userQuest.schema';
import { useTranslations } from 'next-intl';
import { use, useCallback, useEffect, useState } from 'react';
import { enableOneSignalPushFromGesture, isOneSignalSdkReady, subscribeToOneSignalReady } from '@/components/entities/analytics/components/OneSIgnal/utils/oneSignal.client';
import { PwaInstallContext } from '@/components/features/pwa/pwaInstallContext';
import { SliderWrapper } from '@/components/shared/SliderWrapper';
import Toast from '@/components/ui/Toast/toast';
import { Env } from '@/consts/Env';
import { useIsMobile } from '@/hooks/useIsMobile';
import { AppRoutes } from '@/i18n/constants';
import { QueriesKeys } from '@/queries/queriesKeys';
import { getQueryClient } from '@/queries/queryClient';
import { tokenManager } from '@/queries/token-manager';
import { userApi } from '@/queries/user/user.api';
import { useUserMeQuery } from '@/queries/user/user.queries';
import { cn } from '@/utils/cn';
import { welcomeMissionOrder } from './welcomeMission.consts';
import { getWelcomeMissionKind, isMissionCompleted } from './welcomeMission.utils';
import { WelcomeMissionCard } from './WelcomeMissionCard';

interface WelcomeMissionsProps {
  missions: UserQuestDto[];
}

export const WelcomeMissions = ({ missions }: WelcomeMissionsProps) => {
  const tPwa = useTranslations('pwa');
  const tWelcome = useTranslations('gamification.mission-group.welcome-missions');
  const isMobile = useIsMobile();
  const pwaInstall = use(PwaInstallContext);
  const { data: user } = useUserMeQuery();
  const [isSettingMarketingEmail, setIsSettingMarketingEmail] = useState(false);
  const [isPushSdkReady, setIsPushSdkReady] = useState(() => isOneSignalSdkReady());

  useEffect(() => {
    if (isOneSignalSdkReady()) {
      setIsPushSdkReady(true);
      return;
    }

    return subscribeToOneSignalReady(() => {
      setIsPushSdkReady(true);
    });
  }, []);

  const missionsByKind = new Map<WelcomeMissionKind, UserQuestDto>();
  missions.forEach((mission) => {
    const kind = getWelcomeMissionKind(mission);
    if (kind) {
      missionsByKind.set(kind, mission);
    }
  });

  const handlePushAction = useCallback(() => {
    if (!Env.NEXT_PUBLIC_ONE_SIGNAL_APP_ID) {
      return;
    }

    void enableOneSignalPushFromGesture({ id: tokenManager.getUserId() ?? undefined }).then((result) => {
      if (!result.enabled) {
        Toast({
          type: result.reason === 'permission_denied' ? 'warning' : 'error',
          title: result.reason === 'permission_denied' ? tPwa('push-permission-title') : tPwa('push-error-title'),
          description: result.reason === 'permission_denied' ? tPwa('push-permission-description') : tPwa('push-error-description'),
        });
      }
    }).catch(() => {
      Toast({
        type: 'error',
        title: tPwa('push-error-title'),
        description: tPwa('push-error-description'),
      });
    });
  }, [tPwa]);

  const acceptGetOffersConsent = useCallback(() => {
    setIsSettingMarketingEmail(true);
    void userApi.setAllowedSendMarketingEmails({ isAllowed: true })
      .then((user) => {
        const queryClient = getQueryClient();
        queryClient.setQueryData([QueriesKeys.user], user);
        queryClient.invalidateQueries({ queryKey: [QueriesKeys.mePlayerQuests] });
      })
      .finally(() => {
        setIsSettingMarketingEmail(false);
      });
  }, []);

  const handleGetOffersAction = useCallback(() => {
    Toast({
      id: 'welcome-mission-get-offers-consent',
      type: 'success',
      title: tWelcome('marketing-consent-toast.title'),
      description: tWelcome('marketing-consent-toast.description'),
      rejectButtonText: tWelcome('marketing-consent-toast.reject'),
      acceptButtonText: tWelcome('marketing-consent-toast.accept'),
      onAccept: acceptGetOffersConsent,
      disableTimer: true,
    });
  }, [acceptGetOffersConsent, tWelcome]);

  const handleGetAppAction = useCallback(() => {
    if (pwaInstall) {
      pwaInstall.startInstallFlow();
      return;
    }

    window.location.assign(AppRoutes.install);
  }, [pwaInstall]);

  const actions: Record<WelcomeMissionKind, VoidFunction | undefined> = {
    'get-the-app': handleGetAppAction,
    'get-offers': handleGetOffersAction,
    'enable-push': handlePushAction,
  };
  const isAppInstalled = user?.isAppInstalled === true || pwaInstall?.isStandalone === true;
  const visibleMissionKinds = welcomeMissionOrder.filter((kind) => {
    const mission = missionsByKind.get(kind);

    if (!mission || isMissionCompleted(mission)) {
      return false;
    }

    return kind !== 'get-offers' || user?.isAllowedSendAdwEmail === false;
  });

  if (!visibleMissionKinds.length) {
    return null;
  }

  const Wrapper = isMobile ? SliderWrapper : 'div';
  return (
    <div className={cn(
      isMobile && 'relative -mx-4 overflow-x-clip lg:-mx-10 3xl:mr-0',
    )}
    >

      <Wrapper className="max-lg:py-3 max-lg:-my-3">
        <div className="grid grid-cols-3 w-max lg:w-full px-4 lg:px-0 gap-3 grow flex-1 max-lg:min-w-[stretch]">
          {visibleMissionKinds.map((kind) => {
            const mission = missionsByKind.get(kind)!;
            const isEnablePushLocked = kind === 'enable-push' && !isMissionCompleted(mission) && !isAppInstalled;

            return (
              <WelcomeMissionCard
                key={mission.id}
                kind={kind}
                mission={mission}
                onAction={actions[kind]}
                actionDisabled={(kind === 'get-offers' && isSettingMarketingEmail) || (kind === 'enable-push' && !isPushSdkReady)}
                locked={isEnablePushLocked}
              />
            );
          })}
        </div>
      </Wrapper>

    </div>
  );
};
